/**
 * 下書き以外の札を開いた時の案内 (spec `spot-bidding.md` §9 AC-BSP-066) を出すかを
 * 決める純関数。
 *
 * 案内は「なぜ編集できないか」 だけでなく「どうすれば下書きに戻せるか」 (申請取消 /
 * 取下) まで出すので、 **その手順を実行できない札には出さない** (AC-BSP-076 鉄則1:
 * 実行できない手順を案内しない)。 判定の分岐を UI から切り離して、 ページ経由では
 * 組みにくい状態 (締切後・確定済ワークフロー 等) も含めて全分岐を F1 で固定
 * する ([[resolveClearAllState]] と同じ方針)。「入札中」 かどうか (送信軸・約定軸) は
 * backend の取下可否 (`withdrawable`) で決まり、 画面は再導出しない。
 *
 * 判定軸が承認軸だけで閉じないのは、 AC が列挙する 4 状態 (承認申請中 / 承認済 /
 * 差戻し / 入札中) と承認軸 `BiddingApprovalState` が 1:1 でないため:
 *
 * - 承認申請中 / 承認済 / 差戻し は PENDING / APPROVED / REJECTED と 1:1
 * - **入札中は承認軸 SUBMITTED の部分集合**。 spec §6 AC-BSP-047 の「入札中」 は
 *   *入札締切前*で JEPX が入札番号を発番済 (= eSLJ 受理済)・約定処理待ちの札に
 *   限られ、 送信待ち / 送信中 / 送信失敗 / 約定待ち / 全約定 / 未約定 / 失効 /
 *   予約中 も承認軸は SUBMITTED のまま (backend も「提出済 = ApprovalStateSubmitted
 *   && BidSendStatusSent。 承認軸だけではない」 と定める:
 *   `go/internal/backend/domain/valueobject/bid_send_status.go`)
 *
 * 締切後の札も一覧の行クリックで表示専用モーダルが開く (#5356 Des 裁定「全行開く・
 * 下書き以外は表示専用」) ため、 承認軸だけで判定すると全約定済の札にまで
 * 「入札中は『取下』(入札締切前のみ) で下書きに戻して」 と案内してしまう。
 */
import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'

import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'

/** 承認軸から見た AC-BSP-066 の案内対象区分。 */
type NoticeTarget =
  /**
   * AC-BSP-066 が literal で列挙する承認軸の 3 状態 (承認申請中 / 承認済 / 差戻し)。
   *
   * このうち実際に下書きへ戻せるのは 承認申請中 (申請取消 AC-BSP-055) と
   * 差戻し (再編集 AC-BSP-056) の 2 つで、 **承認済には下書きへ戻す経路が無い**
   * (AC-BSP-058 は承認済の 取下・下書き戻しを拒否し、 AC-BSP-059 は承認済を
   * 「JEPX 送信進行中の過渡状態でユーザー操作機会なし」 と定める)。 それでも案内
   * するのは AC-BSP-066 が 4 状態を literal で列挙しているため (§9 が文言と契機の
   * SoT、 #3211)。 「承認軸だけで下書きへ戻せると決まる区分」 ではないので、 今後
   * 状態が増えた時はこの区分を不変条件として流用せず AC の列挙を見ること。
   */
  | 'listedByAc'
  /** 提出済。「入札中」 (= JEPX 受理済・約定処理前) に絞り込めた時だけ案内する。 */
  | 'whileBidding'
  /** 案内しない (未設定 / 下書き / 終端状態)。 */
  | 'never'

/**
 * 承認状態 → 案内対象区分の網羅写像。
 */
const NOTICE_TARGET_BY_APPROVAL_STATE: Record<
  BiddingApprovalState,
  NoticeTarget
> = {
  [BiddingApprovalState.UNSPECIFIED]: 'never',
  // 下書き = 編集できる状態。 案内する事象そのものが起きない。
  [BiddingApprovalState.DRAFT]: 'never',
  [BiddingApprovalState.PENDING]: 'listedByAc',
  [BiddingApprovalState.APPROVED]: 'listedByAc',
  [BiddingApprovalState.REJECTED]: 'listedByAc',
  [BiddingApprovalState.SUBMITTED]: 'whileBidding',
  // 終端状態。 申請取消・取下のどちらでも下書きへ戻せない。
  [BiddingApprovalState.FAILED]: 'never',
  [BiddingApprovalState.CANCELLED]: 'never',
}

/**
 * AC-BSP-066 の案内を表示するか。
 *
 * @param bid 開いている札。 `undefined` = 新規作成 (AC-BSP-066 の対象外。 締切前なら
 *   既存札の状態に関わらず追加できる = AC-BSP-079)。
 * @param isBeforeDeadline 入札締切前か (= 呼び出し側の市場フェーズ判定 `pre` / `open`)。
 * @param isWorkflowReadonly ワークフロー経由で画面が read-only か
 *   (`?workflowId=` の完了済ケース / ケース状態未取得の fail-closed)。
 */
export const shouldShowNonDraftBidNotice = ({
  bid,
  isBeforeDeadline,
  isWorkflowReadonly,
}: {
  bid: Pick<SpotBidRow, 'approvalState' | 'withdrawable'> | undefined
  isBeforeDeadline: boolean
  isWorkflowReadonly: boolean
}): boolean => {
  if (bid === undefined) return false
  // 下書きへ戻す 3 経路 — 申請取消 (AC-BSP-055) / 再編集 (AC-BSP-056) / 取下
  // (AC-BSP-057) — はいずれも入札締切前のみ実行可能 (AC-BSP-055c / AC-BSP-056c /
  // AC-BSP-057 は締切後を backend で拒否し、 画面も導線を出さない)。 締切後に案内すると
  // 状態によらず実行できない手順になるので、 締切後は本案内を出さない
  // (締切後に編集できない旨の表示は AC-BSP-064 の領分)。
  if (!isBeforeDeadline) return false
  // 確定済ワークフロー経由の札も同じ 3 経路を backend が拒否し (AC-BSP-058)、
  // 画面も申請取消・取下の導線を出さない。 案内だけ出すと「ボタンは無いのに
  // 手順は書いてある」 状態になる (domain-knowledge「取消可否の判定は 1 経路に
  // 集約する」)。
  if (isWorkflowReadonly) return false
  const target = NOTICE_TARGET_BY_APPROVAL_STATE[bid.approvalState]
  if (target !== 'whileBidding') return target === 'listedByAc'
  // 入札中 (= 取下の案内が実行できる) かは backend の取下可否で決まる。
  // 取下を始められる札 = 提出済 × JEPX 受理済 × 約定待ち × 締切前 であり、 案内する
  // 「入札中」 の定義 (AC-BSP-047) と一致する。
  return bid.withdrawable === true
}
